'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { FunnelX, MagnifyingGlass } from '@phosphor-icons/react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import 'react-loading-skeleton/dist/skeleton.css';
import * as yup from 'yup';

import {
  Aviso,
  Botao,
  BotaoVoltarProTopo,
  Input,
  Link,
  RadioGroup,
  Tabela,
  ToastController,
} from '@components';
import { Itens, TituloTabela } from '@components/tabela/interfaces';
import { useBetterMediaQuery } from '@helpers';
import { Diario, Diarios, PaginasDiarios, TipoBuscaEnum, TipoPesquisaEnum } from '@interfaces';
import { MascaraUtil } from '@utils';

import { BuscaPorTexto } from './components/busca-por-texto';
import { CardDeResultado } from './components/card-de-resultados';
import * as Estilos from './styles';

interface ParametrosDeBusca {
  numeroDiario?: string;
  texto?: string;
  tipoBusca?: string;
  dataInicial?: string;
  dataFinal?: string;
  evento?: Event;
}

interface ParametrosTabelaSimples {
  diarios: Diario[];
}

interface ValoresFormulario {
  numeroDiario?: string;
  texto?: string;
  dataInicial?: string;
  dataFinal?: string;
  tipoBuscaTexto?: string;
}

export const DiarioOficialView = ({ diarios }: ParametrosTabelaSimples) => {
  const [carregando, atribuirCarregando] = useState(true);
  const [itens, atribuirItens] = useState<Itens<Diario>[]>([]);
  const [titulos, atribuirTitulos] = useState<TituloTabela[]>([]);
  const [atualizarRadio, atribuirAtualizarRadio] = useState<boolean>(false);

  const [pagina, atribuirPagina] = useState(1);
  const [registrosPorPagina, atribuirRegistrosPorPagina] = useState(10);
  const [totalDeRegistros, atribuirTotalDeRegistros] = useState(0);

  const [tipoPesquisa, atribuirTipoPesquisa] = useState<TipoPesquisaEnum | undefined>(undefined);
  const [valoresNosFiltros, atribuirValoresDoFormulario] = useState<ValoresFormulario>({});
  const [parametros, atribuirParametros] = useState<ParametrosDeBusca | object>();

  const [ordenacaoResetada, atribuirOrdenacaoResetada] = useState(false);

  const medidaParaRadioButtonTablete = useBetterMediaQuery('(max-width: 860px)');

  const medidaParaPaginacaoMobile = useBetterMediaQuery('(max-width: 600px)');

  const esquema = yup.object().shape({
    numeroDiario: yup.string().nullable(),
    texto: yup.string(),
    dataInicial: yup
      .date()
      .test('data-valida', 'A data inicial não pode ser maior que a data atual', () => {
        if (getValues('dataInicial')?.trim() !== '') {
          if (moment(getValues('dataInicial')).isBefore(new Date())) {
            return true;
          }
        } else {
          return true;
        }

        return false;
      })
      .transform((value) => {
        if (getFieldState('numeroDiario').isDirty && !moment(value).isValid()) {
          limparInputData('dataInicial');
        }
      })
      .nullable(),
    dataFinal: yup
      .date()
      .test('data-fim', 'A data final não pode ser menor que a data inicial.', () => {
        if (getValues('dataInicial') !== '' && getValues('dataFinal') !== '') {
          if (moment(getValues('dataFinal')).isSameOrAfter(moment(getValues('dataInicial')))) {
            return true;
          }
        } else {
          return true;
        }

        return false;
      })
      .test('valida-data-atual', 'A data final não pode ser maior que a data atual', () => {
        if (getValues('dataFinal') !== '') {
          if (moment(getValues('dataFinal')).isBefore(new Date())) {
            return true;
          }
        } else {
          return true;
        }

        return false;
      })
      .transform((value) => {
        if (getFieldState('numeroDiario').isDirty && !moment(value).isValid()) {
          limparInputData('dataFinal');
        }
      }),
    tipoBusca: yup.string(),
  });

  const {
    control,
    reset,
    getFieldState,
    handleSubmit,
    resetField,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ParametrosDeBusca>({
    resolver: yupResolver(esquema as yup.ObjectSchema<ParametrosDeBusca>),
    mode: 'all',
    defaultValues: {
      numeroDiario: '',
      texto: '',
      dataInicial: '',
      dataFinal: '',
      tipoBusca: 'Busca Exata',
    },
  });

  useEffect(() => {
    if (tipoPesquisa === undefined) {
      return;
    }

    switch (tipoPesquisa) {
      case TipoPesquisaEnum.geral:
        obterTodos();
        break;
      case TipoPesquisaEnum.periodo:
        obterPorPeriodo({
          dataInicial: (parametros as ParametrosDeBusca).dataInicial,
          dataFinal: (parametros as ParametrosDeBusca).dataFinal,
        });
        break;
      default:
        obterPorParametros(parametros as ParametrosDeBusca);
        break;
    }
  }, [pagina, registrosPorPagina]);

  const resultadoBuscaComTexto = (
    data: string,
    descricao: string,
    pagina: number,
    nomeArquivo: string,
    ocorrencia: string,
    endereco: string,
    textos: string[],
  ) => {
    return (
      <BuscaPorTexto
        data={data}
        nomeArquivo={nomeArquivo}
        descricao={descricao}
        pagina={pagina}
        ocorrencia={ocorrencia}
        endereco={endereco}
        textos={textos}
      />
    );
  };

  const obterItensTabelaSimples = (diarios: Diario[]) => {
    const itens = diarios.length
      ? diarios?.map(
          ({ caminhoArquivo, dataPublicacao, descricao, id, ...rest }) =>
            ({
              id,
              objeto: {
                caminhoArquivo,
                dataPublicacao,
                descricao,
                id,
                ...rest,
              },
              valores: [
                {
                  tipo: 'componente',
                  tituloMobile: 'Descrição',
                  valor: <Link endereco={caminhoArquivo} titulo={descricao} />,
                  alinhamento: 'esquerdo',
                  largura: 75,
                },
                {
                  tipo: 'texto',
                  tituloMobile: 'Data',
                  valor: moment(
                    dataPublicacao,
                    ['DD-MM-YYYY HH:mm', 'DD/MM/YYYY HH:mm:ss'],
                    true,
                  ).format('DD/MM/YYYY'),
                  alinhamento: 'centro',
                  largura: 25,
                },
              ],
            } as Itens<Diario>),
        )
      : [];

    const titulos: TituloTabela[] = [
      {
        titulo: 'Número do diário',
        alinhamento: 'esquerdo',
        largura: 75,
      },
      {
        titulo: 'Data',
        alinhamento: 'centro',
        largura: 25,
      },
    ];

    return { itens, titulos };
  };

  const obterRecentes = async () => {
    atribuirCarregando(true);

    await fetch(`/api/diarios-recentes`, {
      method: 'GET',
      cache: 'no-store',
    })
      .then(async (resposta) => {
        if (!resposta.ok) {
          ToastController.erro('Não foi possível obter os diários.');

          return;
        }

        const recentes: Diario[] = await resposta.json();

        const { itens } = obterItensTabelaSimples(recentes)

        atribuirItens(itens)
      })
      .finally(() => atribuirCarregando(false));
  };

  const obterPorPeriodo = async ({
    dataInicial,
    dataFinal,
  }: {
    dataInicial?: string;
    dataFinal?: string;
    }) => {
    atribuirCarregando(true);

    const parametros = new URLSearchParams({
      pagina: String(pagina),
      registrosPorPagina: String(registrosPorPagina),
    });

    if (dataInicial) {
      parametros.append('dataInicial', dataInicial);
    }

    if (dataFinal) {
      parametros.append('dataFinal', dataFinal);
    }

    await fetch(`/api/diarios/listar-todos/?${parametros}`, {
      method: 'GET',
      cache: 'no-store',
    })
      .then(async (resposta) => {
        const dados: Diarios = await resposta.json();

        const { diario, totalDeRegistros } = dados;

        const { itens, titulos } = obterItensTabelaSimples(diario);

        atribuirItens(itens);
        atribuirTitulos(titulos);

        atribuirTotalDeRegistros(totalDeRegistros);
      })
      .finally(() => atribuirCarregando(false));
  };

  const obterTodos = async () => {
    atribuirCarregando(true);

    await fetch(
      `/api/diarios/listar-todos/?${new URLSearchParams({
        pagina: String(pagina),
        registrosPorPagina: String(registrosPorPagina),
      })}`,
      {
        method: 'GET',
        cache: 'no-store',
      },
    )
      .then(async (resposta) => {
        if (resposta.status !== 200) {
          ToastController.erro('Não foi possível obter os diários.');

          return;
        }

        const dados: Diarios = await resposta.json();

        const { diario, totalDeRegistros } = dados;

        const { itens, titulos } = obterItensTabelaSimples(diario);

        atribuirItens(itens);
        atribuirTitulos(titulos);

        atribuirTotalDeRegistros(totalDeRegistros);
      })
      .finally(() => atribuirCarregando(false));
  };

  const obterPorNumero = async (numero: string) => {
    atribuirCarregando(true);

    await fetch(
      `/api/diarios/busca-por-numero/?${new URLSearchParams({
        numero,
      })}`,
      {
        method: 'GET',
        cache: 'no-store',
      },
    )
      .then(async (resposta): Promise<void> => {
        if (resposta.status !== 200) {
          atribuirItens([]);

          atribuirCarregando(false);
        } else {
          const diarios: Diario[] = await resposta.json();

          const { itens, titulos } = obterItensTabelaSimples(diarios);

          atribuirItens(itens);
          atribuirTitulos(titulos);
        }
      })
      .finally(() => atribuirCarregando(false));
  };

  const obterPorParametros = async ({
    dataFinal,
    dataInicial,
    numeroDiario,
    texto,
    tipoBusca,
  }: ParametrosDeBusca) => {
    atribuirCarregando(true);

    const tipo = TipoBuscaEnum[tipoBusca as keyof object];

    const parametros = new URLSearchParams();

    parametros.append('tipo', tipo);

    if (texto) {
      parametros.append('texto', texto);
    }

    if (dataFinal) {
      parametros.append('dataFinal', dataFinal);
    }

    if (dataInicial) {
      parametros.append('dataInicial', dataInicial);
    }

    if (numeroDiario) {
      parametros.append('numero', numeroDiario);
    }

    if (!ordenacaoResetada) {
      parametros.append('pagina', pagina?.toString());
    }

    parametros.append('registrosPorPagina', registrosPorPagina.toString());

    await fetch(`/api/diarios/busca-diarios?${parametros}`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Component-Type': 'application/json',
      },
    })
      .then(async (resposta) => {
        const dados = await resposta.json();

        const { paginasDiario, totalDeRegistros } = dados as PaginasDiarios;

        const itens = paginasDiario?.map(
          (
            {
              caminhoArquivo,
              dataPublicacao,
              descricao,
              hiHighlight,
              nomeArquivo,
              ocorrenciasPorPagina,
              pagina,
              ...rest
            },
            indice,
          ) =>
            ({
              id: indice,
              objeto: {
                caminhoArquivo,
                dataPublicacao,
                descricao,
                hiHighlight,
                nomeArquivo,
                ocorrenciasPorPagina,
                pagina,
                ...rest,
              },
              valores: [
                {
                  tipo: 'componente',
                  tituloMobile: 'Descrição',
                  valor: resultadoBuscaComTexto(
                    moment(dataPublicacao).format('DD/MM/YYYY'),
                    descricao,
                    pagina,
                    nomeArquivo,
                    ocorrenciasPorPagina.toString(),
                    caminhoArquivo,
                    hiHighlight.texto,
                  ),
                  alinhamento: 'esquerdo',
                  largura: 100,
                },
              ],
            } as Itens<Diario>),
        );

        const titulos: TituloTabela[] = [
          {
            titulo: 'Descrição',
            alinhamento: 'esquerdo',
            largura: 100,
          },
        ];

        atribuirItens(itens);
        atribuirTitulos(titulos);
        atribuirTotalDeRegistros(totalDeRegistros);
      })
      .finally(() => atribuirCarregando(false));
  };

  const submeterFormulario = (parametros: ParametrosDeBusca) => {
    voltaParaTopo();

    const valores = getValues();

    resetarPaginacao();

    atribuirOrdenacaoResetada(true);

    const { dataFinal, dataInicial, numeroDiario, texto, tipoBusca } = valores;

    if (numeroDiario && !texto) {
      atribuirTipoPesquisa(TipoPesquisaEnum.numerica);

      atribuirParametros({ numeroDiario });

      atribuirValoresDoFormulario({
        numeroDiario,
      });

      obterPorNumero(numeroDiario);

      return;
    }

    if (texto) {
      if (!numeroDiario && !dataInicial && !dataFinal && texto.trim() === '') {
        parametros.evento?.preventDefault();

        reset({
          texto: '',
        });

        atribuirParametros({});
        atribuirTipoPesquisa(TipoPesquisaEnum.geral);

        obterTodos();

        return;
      } else {
        atribuirTipoPesquisa(TipoPesquisaEnum.textual);

        if (texto.trim() === '') {
          resetField('texto');
          if (numeroDiario) {
            atribuirTipoPesquisa(TipoPesquisaEnum.numerica);

            atribuirParametros({ numeroDiario });

            atribuirValoresDoFormulario({
              numeroDiario,
            });

            obterPorNumero(numeroDiario);

            return;
          }

          if (dataInicial || dataFinal) {
            obterPorPeriodo({ dataInicial, dataFinal });

            atribuirValoresDoFormulario({
              dataInicial,
              dataFinal,
            });

            atribuirParametros({ dataInicial, dataFinal });
            atribuirTipoPesquisa(TipoPesquisaEnum.periodo);

            return;
          }
        }

        atribuirParametros({ texto, dataFinal, dataInicial, tipoBusca, numeroDiario });

        atribuirValoresDoFormulario({
          numeroDiario,
          texto,
          dataInicial,
          dataFinal,
          tipoBuscaTexto: TipoBuscaEnum[tipoBusca as keyof object],
        });

        obterPorParametros({ texto, dataFinal, dataInicial, tipoBusca, numeroDiario });

        return;
      }
    }

    if (dataInicial || dataFinal) {
      obterPorPeriodo({ dataInicial, dataFinal });

      atribuirValoresDoFormulario({
        dataInicial,
        dataFinal,
      });

      atribuirParametros({ dataInicial, dataFinal });
      atribuirTipoPesquisa(TipoPesquisaEnum.periodo);

      return;
    }

    atribuirParametros({});
    atribuirTipoPesquisa(TipoPesquisaEnum.geral);

    obterTodos();
  };

  const limparTipoDeBusca = () => {
    atribuirAtualizarRadio(true);

    setTimeout(() => {
      atribuirAtualizarRadio(false);
    }, 1000);
  };

  const resetarPaginacao = () => {
    atribuirPagina(1);
    atribuirRegistrosPorPagina(10);
    atribuirTotalDeRegistros(0);
  };

  const limparInputData = (nome: string) => {
    const input = document.querySelector(`input[name="${nome}"]`) as HTMLInputElement;

    input.value = '';
  };

  const limparDatas = () => {
    limparInputData('dataInicial');
    limparInputData('dataFinal');
  };

  const limparFiltros = () => {
    reset(
      {
        texto: '',
        numeroDiario: '',
        dataInicial: '',
        dataFinal: '',
        tipoBusca: 'Busca Exata',
      },
      {
        keepDirty: false,
      },
    );

    limparTipoDeBusca();
    resetarPaginacao();
    limparDatas();

    atribuirParametros(undefined);

    atribuirTipoPesquisa(undefined);

    obterRecentes();
  };

  const voltaParaTopo = () => {
    const scrollTela = document.documentElement.scrollTop || document.body.scrollTop;

    if (medidaParaRadioButtonTablete) {
      const elemento = window.document.getElementById('coluna-diarios');

      if (elemento) {
        elemento.scrollIntoView({ behavior: 'smooth', block: 'start' });
        elemento.focus();
      }
    }

    if (scrollTela > 300 && !medidaParaRadioButtonTablete) {
      const container =
        document.documentElement.scrollTop > 0 ? document.documentElement : document.body;
      container!.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Estilos.Global />

      <Estilos.Conteiner>
        <Estilos.Filtros>
          <Estilos.Formulario onSubmit={handleSubmit(submeterFormulario)}>
            <h2>Buscar por:</h2>

            <Input
              control={control}
              rotulo='Número do diário'
              tipo='tel'
              nome='numeroDiario'
              id='numero-diario'
              tamanho='medio'
              placeholder='12345'
              removerMascara={(valor) => MascaraUtil.removerMascara(valor ?? '')}
              mascara={(valor) => MascaraUtil.numero(valor ?? '')}
              onInput={() => {
                resetField('dataInicial');
                resetField('dataFinal');
              }}
            />

            <Estilos.ItemFormulario>
              <Estilos.LinhaDatas>
                <Input
                  control={control}
                  min='1900-01-01'
                  max={moment().format('YYYY-MM-DD')}
                  rotulo='Data inicial'
                  tipo='date'
                  nome='dataInicial'
                  id='data-inicial'
                  tamanho='medio'
                  erro={errors.dataInicial?.message?.toString()}
                  desabilitado={!!watch('numeroDiario') && getFieldState('numeroDiario').isDirty}
                />
                <Input
                  control={control}
                  min='1900-01-01'
                  max={moment().format('YYYY-MM-DD')}
                  rotulo='Data final'
                  tipo='date'
                  nome='dataFinal'
                  id='data-final'
                  tamanho='medio'
                  erro={errors.dataFinal?.message?.toString()}
                  desabilitado={!!watch('numeroDiario') && getFieldState('numeroDiario').isDirty}
                />
              </Estilos.LinhaDatas>

              {!!watch('numeroDiario') && getFieldState('numeroDiario').isDirty && (
                <p>
                  Para utilizar os filtros de data, é necessário apagar o valor inserido no filtro
                  Número do diário.
                </p>
              )}
            </Estilos.ItemFormulario>

            <Input
              control={control}
              rotulo='Texto ou palavra'
              tipo='text'
              nome='texto'
              id='texto'
              tamanho='medio'
              placeholder='O que deseja encontrar?'
              textoAuxiliar='Busca por texto do Diário Oficial somente em publicações a partir de janeiro de 2007.'
              onChangeCapture={() => {
                atribuirPagina(1);
                atribuirRegistrosPorPagina(10);
              }}
            />
            <RadioGroup
              controle={control}
              orientacao={'horizontal'}
              nome='tipoBusca'
              id='tipo-busca'
              opcoes={[
                {
                  id: 'buscaExata',
                  rotulo: 'Busca Exata',
                },
                {
                  id: 'umaPalavra',
                  rotulo: 'Ao menos uma palavra',
                },
              ]}
              padrao='buscaExata'
              reset={atualizarRadio}
            ></RadioGroup>

            <Aviso
              descricaoComHtml={
                <span>
                  Busca por texto do Diário Oficial somente em publicações a partir de{' '}
                  <strong>janeiro de 2007</strong>
                </span>
              }
            />

            <Estilos.ConteinerBotao>
              <Botao
                preenchimento='solido'
                texto='Buscar'
                tamanho='medio'
                id='buscar'
                tipo='submit'
                Icone={MagnifyingGlass}
              />
              <Botao
                preenchimento='contorno'
                texto='Limpar filtros'
                tamanho='medio'
                id='limparFiltros'
                Icone={FunnelX}
                onClick={() => {
                  const container =
                    document.documentElement.scrollTop > 0
                      ? document.documentElement
                      : document.body;
                  container!.scrollTo({ top: 0, behavior: 'smooth' });

                  limparFiltros();
                }}
              />
            </Estilos.ConteinerBotao>
          </Estilos.Formulario>
        </Estilos.Filtros>

        {parametros ? (
          <Estilos.Coluna id='coluna-diarios'>
            {Object.keys(parametros).length !== 0 && (
              <CardDeResultado valores={valoresNosFiltros} tipoPesquisa={tipoPesquisa} />
            )}

            <Tabela
              id='diarios'
              nome='Tabela de resultado da busca'
              titulos={titulos}
              itens={itens}
              carregando={carregando}
              pagina={pagina}
              itensPorPagina={registrosPorPagina}
              totalDeRegistros={totalDeRegistros}
              ocultarControleDePaginacao={totalDeRegistros > 5 ? false : true}
              aoMudarPagina={(pagina: number) => {
                voltaParaTopo();

                atribuirOrdenacaoResetada(false);
                atribuirPagina(pagina);
              }}
              aoMudarQuantidadeDeItens={(quantidade: number) => {
                atribuirRegistrosPorPagina(quantidade);
                atribuirPagina(1);
              }}
              mensagemTabelaVazia='Nenhum registro encontrado.'
              exibirTodasAsPaginas={!!medidaParaPaginacaoMobile}
            />
          </Estilos.Coluna>
        ) : (
          <Estilos.Coluna id='coluna-diarios'>
            <h2 id='titulo-diarios'>Últimas Edições Publicadas</h2>

            <Tabela
              id='diarios'
              nome='Últimos Diários Publicados'
              titulos={[
                {
                  titulo: 'Número do diário',
                  alinhamento: 'esquerdo',
                  largura: 75,
                },
                {
                  titulo: 'Data',
                  alinhamento: 'centro',
                  largura: 25,
                },
              ]}
              itens={itens && itens.length ? itens : obterItensTabelaSimples(diarios).itens}
              ocultarControleDePaginacao={true}
              mensagemTabelaVazia='Nenhum registro encontrado.'
            />
          </Estilos.Coluna>
        )}
        <BotaoVoltarProTopo />
      </Estilos.Conteiner>
    </>
  );
};
